import {
  actionCreators as assignmentFormActions,
  selectAssignment,
} from '@app/components/AssignmentForm/modules/assignmentForm'
import { Assignment } from '@app/models/Assignment'
import { AssignmentSource } from '@app/models/AssignmentSource'
import { AssociatedContact } from '@app/models/AssociatedContact'
import { AssociatedProperty } from '@app/models/AssociatedProperty'
import {
  BrokeragePipeline,
  IBrokeragePipeline,
} from '@app/models/BrokeragePipeline'
import { BrokeragePipelineCommissionFee } from '@app/models/BrokeragePipelineCommissionFee'
import { Company } from '@app/models/Company'
import { AssignmentSourceType, ListingType } from '@app/models/enums'
import { LeasePriceTerm } from '@app/models/LeasePriceTerm'
import { ApplicationState } from '@app/store'
import { convertToMarkup } from '@app/utils/mention'
import { errorToast, successToast } from '@app/utils/notifications'
import {
  IRequestOptions,
  makeAuthenticatedRequest,
  RequestMethod,
} from '@app/utils/request'
import { List, Record } from 'immutable'
import { push } from 'connected-react-router'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'
import { IBrokeragePipelineCriticalDates } from '@app/models/BrokeragePipelineCriticalDates'

// -------------------------
// STATE
// -------------------------

export interface IPipelineEditState {
  readonly editCriticalDateIndex: number
  readonly hasExistingPipeline: boolean
  readonly error?: string
  readonly isLoading: boolean
  readonly isReady: boolean
  readonly isSaving: boolean
  readonly listingType?: ListingType
  readonly pipeline: IBrokeragePipeline
  readonly showCriticalDateModal: boolean
}

const PipelineEditStateRecord = Record({
  editCriticalDateIndex: -1,
  hasExistingPipeline: false,
  error: undefined,
  isLoading: false,
  isReady: false,
  isSaving: false,
  listingType: undefined,
  pipeline: new BrokeragePipeline(),
  showCriticalDateModal: false,
})

export class PipelineEditState extends PipelineEditStateRecord
  implements IPipelineEditState {
  public static fromJS(initialState?: Partial<IPipelineEditState>) {
    if (!initialState) {
      return new PipelineEditState()
    }

    const { pipeline, ...rest } = initialState
    return new PipelineEditState({
      pipeline: BrokeragePipeline.fromJS(pipeline),
      ...rest,
    })
  }

  public readonly editCriticalDateIndex: number
  public readonly hasExistingPipeline: boolean
  public readonly error?: string
  public readonly isLoading: boolean
  public readonly isReady: boolean
  public readonly isSaving: boolean
  public readonly listingType?: ListingType
  public readonly pipeline: BrokeragePipeline
  public readonly showCriticalDateModal: boolean

  constructor(props?: Partial<PipelineEditState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ActionTypes {
  AttachToNewListing = 'PIPELINE_EDIT::ATTACH_TO_NEW_LISTING',
  BeginAddCriticalDate = 'PIPELINE_EDIT::BEGIN_ADD_CRITICAL_DATE',
  BeginEditCriticalDate = 'PIPELINE_EDIT::BEGIN_EDIT_CRITICAL_DATE',
  CancelEditCriticalDate = 'PIPELINE_EDIT::CANCEL_EDIT_CRITICAL_DATE',
  DeleteCriticalDate = 'PIPELINE_EDIT::DELETE_CRITICAL_DATE',
  DeleteLeasePriceTerm = 'PIPELINE_EDIT::DELETE_LEASE_PRICE_TERM',
  EndEditCriticalDate = 'PIPELINE_EDIT::END_EDIT_CRITICAL_DATE',
  CheckPipelineExists = 'PIPELINE_EDIT::CHECK_PIPELINE_EXISTS',
  CheckPipelineExistsFailure = 'PIPELINE_EDIT::CHECK_PIPELINE_EXISTS_FAILURE',
  CheckPipelineExistsSuccess = 'PIPELINE_EDIT::CHECK_PIPELINE_EXISTS_SUCCESS',
  LoadAddPipeline = 'PIPELINE_EDIT::LOAD_ADD_PIPELINE',
  LoadEditPipeline = 'PIPELINE_EDIT::LOAD_EDIT_PIPELINE',
  LoadPipelineFailure = 'PIPELINE_EDIT::LOAD_PIPELINE_FAILURE',
  LoadPipelineRequest = 'PIPELINE_EDIT::LOAD_PIPELINE_REQUEST',
  LoadPipelineSuccess = 'PIPELINE_EDIT::LOAD_PIPELINE_SUCCESS',
  ResetState = 'PIPELINE_EDIT::RESET_STATE',
  SavePipeline = 'PIPELINE_EDIT::SAVE_PIPELINE',
  SavePipelineFailure = 'PIPELINE_EDIT::SAVE_PIPELINE_FAILURE',
  SavePipelineRequest = 'PIPELINE_EDIT::SAVE_PIPELINE_REQUEST',
  SavePipelineSuccess = 'PIPELINE_EDIT::SAVE_PIPELINE_SUCCESS',
  SetBuyers = 'PIPELINE_EDIT::SET_BUYERS',
  SetBuyersLender = 'PIPELINE_EDIT::SET_BUYERS_LENDER',
  SetBuyersTitleCompany = 'PIPELINE_EDIT::SET_BUYERS_TITLE_COMPANY',
  SetCriticalDates = 'PIPELINE_EDIT::SET_CRITICAL_DATES',
  SetClosingDate = 'PIPELINE_EDIT::SET_CLOSING_DATE',
  SetCriticalDate = 'PIPELINE_EDIT::SET_CRITICAL_DATE',
  SetCriticalDateComplete = 'PIPELINE_EDIT::SET_CRITICAL_DATE_COMPLETE',
  SetDueDiligenceExpirationDate = 'PIPELINE_EDIT::SET_DUE_DILIGENCE_EXPIRATION_DATE',
  SetEarnestMoney = 'PIPELINE_EDIT::SET_EARNEST_MONEY',
  SetEarnestMoneyDepositDate = 'PIPELINE_EDIT::SET_EARNEST_MONEY_DEPOSIT_DATE',
  SetEarnestMoneyLocation = 'PIPELINE_EDIT::SET_EARNEST_MONEY_LOCATION',
  SetEarnestMoneyWithdrawlDate = 'PIPELINE_EDIT::SET_EARNEST_MONEY_WITHDRAWL_DATE',
  SetEffectiveDate = 'PIPELINE_EDIT::SET_EFFECTIVE_DATE',
  SetNotes = 'PIPELINE_EDIT::SET_NOTES',
  SetIs1031Exchange = 'PIPELINE_EDIT::SET_IS_1031_EXCHANGE',
  SetLeasePriceTerm = 'PIPELINE_EDIT::SET_LEASE_PRICE_TERM',
  SetListingAgents = 'PIPELINE_EDIT::SET_LISTING_AGENTS',
  SetListingFee = 'PIPELINE_EDIT::SET_LISTING_FEE',
  SetName = 'PIPELINE_EDIT::SET_NAME',
  SetNoListingAgent = 'PIPELINE_EDIT::SET_NO_LISTING_AGENT',
  SetNoSellingAgent = 'PIPELINE_EDIT::SET_NO_SELLING_AGENT',
  SetSalesPrice = 'PIPELINE_EDIT::SET_SALES_PRICE',
  SetSellers = 'PIPELINE_EDIT::SET_SELLERS',
  SetSellersMortgage = 'PIPELINE_EDIT::SET_SELLERS_MORTGAGE',
  SetSellersTitleCompany = 'PIPELINE_EDIT::SET_SELLERS_TITLE_COMPANY',
  SetSellingAgents = 'PIPELINE_EDIT::SET_SELLING_AGENTS',
  SetSellingFee = 'PIPELINE_EDIT::SET_SELLING_FEE',
  SetStatus = 'PIPELINE_EDIT::SET_STATUS',
  ToggleShowCriticalDateModal = 'PIPELINE_EDIT::TOGGLE_SHOW_CRITICAL_DATE_MODAL',
}

interface IAttachNewListingAction {
  type: ActionTypes.AttachToNewListing
}

interface IBeginAddCriticalDateAction {
  type: ActionTypes.BeginAddCriticalDate
}

interface IBeginEditCriticalDateAction {
  criticalDate: Assignment
  type: ActionTypes.BeginEditCriticalDate
}

interface ICancelEditCriticalDateAction {
  type: ActionTypes.CancelEditCriticalDate
}

interface ICheckPipelineExistsAction {
  listingId: string
  type: ActionTypes.CheckPipelineExists
}

interface ICheckPipelineExistsFailureAction {
  error: string
  type: ActionTypes.CheckPipelineExistsFailure
}

interface ICheckPipelineExistsSuccessAction {
  hasExistingPipeline: boolean
  type: ActionTypes.CheckPipelineExistsSuccess
}

interface IDeleteCriticalDateAction {
  criticalDateId: string
  type: ActionTypes.DeleteCriticalDate
}

interface IDeleteLeasePriceTermAction {
  index: number
  type: ActionTypes.DeleteLeasePriceTerm
}

interface IEndEditCriticalDateAction {
  type: ActionTypes.EndEditCriticalDate
}

interface ILoadAddPipelineAction {
  listingId: string
  quickAdd: boolean
  type: ActionTypes.LoadAddPipeline
}

interface ILoadEditPipelineAction {
  pipelineId: string
  type: ActionTypes.LoadEditPipeline
}

interface ILoadPipelineFailureAction {
  error: string
  type: ActionTypes.LoadPipelineFailure
}

interface ILoadPipelineRequestAction {
  type: ActionTypes.LoadPipelineRequest
}

interface ILoadPipelineSuccessAction {
  pipeline: BrokeragePipeline
  type: ActionTypes.LoadPipelineSuccess
}

interface IResetStateAction {
  type: ActionTypes.ResetState
}

interface ISavePipelineAction {
  pipeline: BrokeragePipeline
  type: ActionTypes.SavePipeline
}

interface ISavePipelineFailureAction {
  error: string
  type: ActionTypes.SavePipelineFailure
}

interface ISavePipelineRequestAction {
  type: ActionTypes.SavePipelineRequest
}

interface ISavePipelineSuccessAction {
  type: ActionTypes.SavePipelineSuccess
}

interface ISetBuyersAction {
  buyers: List<AssociatedContact>
  type: ActionTypes.SetBuyers
}

interface ISetBuyersLenderAction {
  buyersLender: string
  type: ActionTypes.SetBuyersLender
}

interface ISetBuyersTitleCompanyAction {
  buyersTitleCompany?: Company
  type: ActionTypes.SetBuyersTitleCompany
}

interface ISetClosingDateAction {
  closingDate?: number
  type: ActionTypes.SetClosingDate
}

interface ISetDueDiligenceExpirationDateAction {
  dueDiligenceExpirationDate?: number
  type: ActionTypes.SetDueDiligenceExpirationDate
}

interface ISetCriticalDateAction {
  criticalDate: Assignment
  type: ActionTypes.SetCriticalDate
}

interface ISetCriticalDateCompleteAction {
  criticalDateId: string
  isComplete: boolean
  type: ActionTypes.SetCriticalDateComplete
}

interface ISetEarnestMoneyAction {
  earnestMoney: number
  type: ActionTypes.SetEarnestMoney
}

interface ISetEarnestMoneyDepositDateAction {
  earnestMoneyDepositDate?: number
  type: ActionTypes.SetEarnestMoneyDepositDate
}

interface ISetEarnestMoneyLocationAction {
  earnestMoneyLocation: string
  type: ActionTypes.SetEarnestMoneyLocation
}

interface ISetEarnestMoneyWithdrawlDateAction {
  earnestMoneyWithdrawlDate?: number
  type: ActionTypes.SetEarnestMoneyWithdrawlDate
}

interface ISetEffectiveDateAction {
  effectiveDate?: number
  type: ActionTypes.SetEffectiveDate
}

interface ISetNotesAction {
  notes: string
  type: ActionTypes.SetNotes
}

interface ISetIs1031ExchangeAction {
  is1031Exchange: boolean
  type: ActionTypes.SetIs1031Exchange
}

interface ISetLeasePriceTermAction {
  index?: number
  term: LeasePriceTerm
  type: ActionTypes.SetLeasePriceTerm
}

interface ISetListingAgentsAction {
  listingAgents: List<AssociatedContact>
  type: ActionTypes.SetListingAgents
}

interface ISetListingFeeAction {
  listingFee: BrokeragePipelineCommissionFee
  type: ActionTypes.SetListingFee
}

interface ISetNameAction {
  name: string
  type: ActionTypes.SetName
}

interface ISetNoListingAgentAction {
  noListingAgent: boolean
  type: ActionTypes.SetNoListingAgent
}

interface ISetNoSellingAgentAction {
  noSellingAgent: boolean
  type: ActionTypes.SetNoSellingAgent
}

interface ISetSalesPriceAction {
  salesPrice: number
  type: ActionTypes.SetSalesPrice
}

interface ISetSellersAction {
  sellers: List<AssociatedContact>
  type: ActionTypes.SetSellers
}

interface ISetSellersMortgageAction {
  sellersMortgage: string
  type: ActionTypes.SetSellersMortgage
}

interface ISetSellersTitleCompanyAction {
  sellersTitleCompany?: Company
  type: ActionTypes.SetSellersTitleCompany
}

interface ISetSellingAgentsAction {
  sellingAgents: List<AssociatedContact>
  type: ActionTypes.SetSellingAgents
}

interface ISetSellingFeeAction {
  sellingFee: BrokeragePipelineCommissionFee
  type: ActionTypes.SetSellingFee
}

interface ISetStatusAction {
  status: string
  type: ActionTypes.SetStatus
}

interface IToggleShowCriticalDateModalAction {
  show: boolean
  type: ActionTypes.ToggleShowCriticalDateModal
}

interface ISetCriticalDatesAction {
  criticalDates: List<Assignment>
  type: ActionTypes.SetCriticalDates
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  //| IAttachNewListingAction
  | IDeleteCriticalDateAction
  | IDeleteLeasePriceTermAction
  | ILoadPipelineFailureAction
  | ILoadPipelineRequestAction
  | ILoadPipelineSuccessAction
  | IResetStateAction
  | ICheckPipelineExistsAction
  | ICheckPipelineExistsFailureAction
  | ICheckPipelineExistsSuccessAction
  | ISavePipelineFailureAction
  | ISavePipelineRequestAction
  | ISavePipelineSuccessAction
  | ISetBuyersAction
  | ISetBuyersLenderAction
  | ISetBuyersTitleCompanyAction
  | ISetClosingDateAction
  | ISetCriticalDatesAction
  | ISetCriticalDateAction
  | ISetCriticalDateCompleteAction
  | ISetDueDiligenceExpirationDateAction
  | ISetEarnestMoneyAction
  | ISetEarnestMoneyDepositDateAction
  | ISetEarnestMoneyLocationAction
  | ISetEarnestMoneyWithdrawlDateAction
  | ISetEffectiveDateAction
  | ISetLeasePriceTermAction
  | ISetListingAgentsAction
  | ISetListingFeeAction
  | ISetNotesAction
  | ISetIs1031ExchangeAction
  | ISetNameAction
  | ISetNoListingAgentAction
  | ISetNoSellingAgentAction
  | ISetSalesPriceAction
  | ISetSellersAction
  | ISetSellersMortgageAction
  | ISetSellersTitleCompanyAction
  | ISetSellingAgentsAction
  | ISetSellingFeeAction
  | ISetStatusAction
  | IToggleShowCriticalDateModalAction

// -------------------------
// ACTION CREATORS
// -------------------------

function beginAddCriticalDate(): IBeginAddCriticalDateAction {
  return { type: ActionTypes.BeginAddCriticalDate }
}

function beginEditCriticalDate(
  criticalDate: Assignment,
): IBeginEditCriticalDateAction {
  return {
    criticalDate,
    type: ActionTypes.BeginEditCriticalDate,
  }
}

function cancelEditCriticalDate(): ICancelEditCriticalDateAction {
  return { type: ActionTypes.CancelEditCriticalDate }
}

function deleteCriticalDate(criticalDateId: string): IDeleteCriticalDateAction {
  return {
    criticalDateId,
    type: ActionTypes.DeleteCriticalDate,
  }
}

function deleteLeasePriceTerm(index: number): IDeleteLeasePriceTermAction {
  return {
    index,
    type: ActionTypes.DeleteLeasePriceTerm,
  }
}

function endEditCriticalDate(): IEndEditCriticalDateAction {
  return {
    type: ActionTypes.EndEditCriticalDate,
  }
}

function checkPipelineExists(listingId: string): ICheckPipelineExistsAction {
  return {
    listingId,
    type: ActionTypes.CheckPipelineExists,
  }
}

function checkPipelineExistsFailure(
  error: string,
): ICheckPipelineExistsFailureAction {
  return {
    error,
    type: ActionTypes.CheckPipelineExistsFailure,
  }
}

function checkPipelineExistsSuccess(
  hasExistingPipeline: boolean,
): ICheckPipelineExistsSuccessAction {
  return {
    hasExistingPipeline,
    type: ActionTypes.CheckPipelineExistsSuccess,
  }
}

function loadAddPipeline(
  listingId: string,
  quickAdd: boolean,
): ILoadAddPipelineAction {
  return {
    listingId,
    quickAdd,
    type: ActionTypes.LoadAddPipeline,
  }
}

function loadEditPipeline(pipelineId: string): ILoadEditPipelineAction {
  return {
    pipelineId,
    type: ActionTypes.LoadEditPipeline,
  }
}

function loadPipelineFailure(error: string): ILoadPipelineFailureAction {
  return {
    error,
    type: ActionTypes.LoadPipelineFailure,
  }
}

function loadPipelineRequest(): ILoadPipelineRequestAction {
  return { type: ActionTypes.LoadPipelineRequest }
}

function loadPipelineSuccess(
  pipeline: BrokeragePipeline,
): ILoadPipelineSuccessAction {
  return {
    pipeline,
    type: ActionTypes.LoadPipelineSuccess,
  }
}

function resetState(): IResetStateAction {
  return { type: ActionTypes.ResetState }
}

function savePipeline(pipeline: BrokeragePipeline): ISavePipelineAction {
  return {
    pipeline,
    type: ActionTypes.SavePipeline,
  }
}

function savePipelineFailure(error: string): ISavePipelineFailureAction {
  return {
    error,
    type: ActionTypes.SavePipelineFailure,
  }
}

function savePipelineRequest(): ISavePipelineRequestAction {
  return { type: ActionTypes.SavePipelineRequest }
}

function savePipelineSuccess(): ISavePipelineSuccessAction {
  return { type: ActionTypes.SavePipelineSuccess }
}

function setBuyers(buyers: List<AssociatedContact>): ISetBuyersAction {
  return {
    buyers,
    type: ActionTypes.SetBuyers,
  }
}

function setBuyersLender(buyersLender: string): ISetBuyersLenderAction {
  return {
    buyersLender,
    type: ActionTypes.SetBuyersLender,
  }
}

function setBuyersTitleCompany(
  buyersTitleCompany?: Company,
): ISetBuyersTitleCompanyAction {
  return {
    buyersTitleCompany,
    type: ActionTypes.SetBuyersTitleCompany,
  }
}

function setClosingDate(closingDate?: number): ISetClosingDateAction {
  return {
    closingDate,
    type: ActionTypes.SetClosingDate,
  }
}

function setCriticalDates(
  criticalDates: List<Assignment>,
): ISetCriticalDatesAction {
  return {
    criticalDates,
    type: ActionTypes.SetCriticalDates,
  }
}

function setCriticalDate(criticalDate: Assignment): ISetCriticalDateAction {
  return {
    criticalDate,
    type: ActionTypes.SetCriticalDate,
  }
}

function setCriticalDateComplete(
  criticalDateId: string,
  isComplete: boolean,
): ISetCriticalDateCompleteAction {
  return {
    criticalDateId,
    isComplete,
    type: ActionTypes.SetCriticalDateComplete,
  }
}

function setDueDiligenceExpirationDate(
  dueDiligenceExpirationDate?: number,
): ISetDueDiligenceExpirationDateAction {
  return {
    dueDiligenceExpirationDate,
    type: ActionTypes.SetDueDiligenceExpirationDate,
  }
}

function setEarnestMoney(earnestMoney: number): ISetEarnestMoneyAction {
  return {
    earnestMoney,
    type: ActionTypes.SetEarnestMoney,
  }
}

function setEarnestMoneyDepositDate(
  earnestMoneyDepositDate?: number,
): ISetEarnestMoneyDepositDateAction {
  return {
    earnestMoneyDepositDate,
    type: ActionTypes.SetEarnestMoneyDepositDate,
  }
}

function setEarnestMoneyLocation(
  earnestMoneyLocation: string,
): ISetEarnestMoneyLocationAction {
  return {
    earnestMoneyLocation,
    type: ActionTypes.SetEarnestMoneyLocation,
  }
}

function setEarnestMoneyWithdrawlDate(
  earnestMoneyWithdrawlDate?: number,
): ISetEarnestMoneyWithdrawlDateAction {
  return {
    earnestMoneyWithdrawlDate,
    type: ActionTypes.SetEarnestMoneyWithdrawlDate,
  }
}

function setEffectiveDate(effectiveDate?: number): ISetEffectiveDateAction {
  return {
    effectiveDate,
    type: ActionTypes.SetEffectiveDate,
  }
}

function setNotes(notes: string): ISetNotesAction {
  return {
    notes,
    type: ActionTypes.SetNotes,
  }
}

function setIs1031Exchange(is1031Exchange: boolean): ISetIs1031ExchangeAction {
  return {
    is1031Exchange,
    type: ActionTypes.SetIs1031Exchange,
  }
}

function setLeasePriceTerm(
  term: LeasePriceTerm,
  index?: number,
): ISetLeasePriceTermAction {
  return {
    index,
    term,
    type: ActionTypes.SetLeasePriceTerm,
  }
}

function setListingAgents(
  listingAgents: List<AssociatedContact>,
): ISetListingAgentsAction {
  return {
    listingAgents,
    type: ActionTypes.SetListingAgents,
  }
}

function setListingFee(
  listingFee: BrokeragePipelineCommissionFee,
): ISetListingFeeAction {
  return {
    listingFee,
    type: ActionTypes.SetListingFee,
  }
}

function setName(name: string): ISetNameAction {
  return {
    name,
    type: ActionTypes.SetName,
  }
}

function setNoListingAgent(noListingAgent: boolean): ISetNoListingAgentAction {
  return {
    noListingAgent,
    type: ActionTypes.SetNoListingAgent,
  }
}

function setNoSellingAgent(noSellingAgent: boolean): ISetNoSellingAgentAction {
  return {
    noSellingAgent,
    type: ActionTypes.SetNoSellingAgent,
  }
}

function setSalesPrice(salesPrice: number): ISetSalesPriceAction {
  return {
    salesPrice,
    type: ActionTypes.SetSalesPrice,
  }
}

function setSellers(sellers: List<AssociatedContact>): ISetSellersAction {
  return {
    sellers,
    type: ActionTypes.SetSellers,
  }
}

function setSellersMortgage(
  sellersMortgage: string,
): ISetSellersMortgageAction {
  return {
    sellersMortgage,
    type: ActionTypes.SetSellersMortgage,
  }
}

function setSellersTitleCompany(
  sellersTitleCompany?: Company,
): ISetSellersTitleCompanyAction {
  return {
    sellersTitleCompany,
    type: ActionTypes.SetSellersTitleCompany,
  }
}

function setSellingAgents(
  sellingAgents: List<AssociatedContact>,
): ISetSellingAgentsAction {
  return {
    sellingAgents,
    type: ActionTypes.SetSellingAgents,
  }
}

function setSellingFee(
  sellingFee: BrokeragePipelineCommissionFee,
): ISetSellingFeeAction {
  return {
    sellingFee,
    type: ActionTypes.SetSellingFee,
  }
}

function setStatus(status: string): ISetStatusAction {
  return {
    status,
    type: ActionTypes.SetStatus,
  }
}

function toggleShowCriticalDateModal(
  show: boolean,
): IToggleShowCriticalDateModalAction {
  return {
    show,
    type: ActionTypes.ToggleShowCriticalDateModal,
  }
}

export const actionCreators = {
  //attachToNewListing,
  beginAddCriticalDate,
  beginEditCriticalDate,
  cancelEditCriticalDate,
  deleteCriticalDate,
  deleteLeasePriceTerm,
  endEditCriticalDate,
  loadAddPipeline,
  loadEditPipeline,
  resetState,
  checkPipelineExists,
  checkPipelineExistsFailure,
  checkPipelineExistsSuccess,
  savePipeline,
  setBuyers,
  setBuyersLender,
  setBuyersTitleCompany,
  setClosingDate,
  setCriticalDates,
  setCriticalDateComplete,
  setDueDiligenceExpirationDate,
  setEarnestMoney,
  setEarnestMoneyDepositDate,
  setEarnestMoneyLocation,
  setEarnestMoneyWithdrawlDate,
  setEffectiveDate,
  setNotes,
  setIs1031Exchange,
  setLeasePriceTerm,
  setListingAgents,
  setListingFee,
  setName,
  setNoListingAgent,
  setNoSellingAgent,
  setSalesPrice,
  setSellers,
  setSellersMortgage,
  setSellersTitleCompany,
  setSellingAgents,
  setSellingFee,
  setStatus,
}

// -------------------------
// REDUCER
// -------------------------

export const reducer: Reducer<PipelineEditState> = (
  state: PipelineEditState = new PipelineEditState(),
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ActionTypes.DeleteCriticalDate: {
      const index = state.pipeline.criticalDates.findIndex(
        (cd) => cd !== undefined && cd.id === action.criticalDateId,
      )
      if (index >= 0) {
        return state
          .setIn(
            ['pipeline', 'deletedCriticalDates'],
            state.pipeline.deletedCriticalDates.push(
              state.pipeline.criticalDates.get(index),
            ),
          )
          .deleteIn(['pipeline', 'criticalDates', index]) as PipelineEditState
      } else {
        return state
      }
    }

    case ActionTypes.DeleteLeasePriceTerm:
      if (state.pipeline.leasePriceBreakdown.has(action.index)) {
        const newLeasePriceBreakdown = state.pipeline.leasePriceBreakdown.delete(
          action.index,
        )
        return state.setIn(
          ['pipeline', 'leasePriceBreakdown'],
          newLeasePriceBreakdown,
        ) as PipelineEditState
      } else {
        return state
      }

    case ActionTypes.LoadPipelineFailure:
      return state
        .set('error', action.error)
        .set('isLoading', false)
        .set('isReady', false) as PipelineEditState

    case ActionTypes.LoadPipelineRequest:
      return state
        .set('isLoading', true)
        .set('isReady', false) as PipelineEditState

    case ActionTypes.CheckPipelineExists:
      return state
        .set('isLoading', true)
        .set('isReady', false) as PipelineEditState

    case ActionTypes.CheckPipelineExistsFailure:
      return state.set('error', action.error) as PipelineEditState

    case ActionTypes.CheckPipelineExistsSuccess:
      return state.set(
        'hasExistingPipeline',
        action.hasExistingPipeline,
      ) as PipelineEditState

    case ActionTypes.LoadPipelineSuccess:
      return state
        .set('error', undefined)
        .set('isLoading', false)
        .set('isReady', true)
        .set('pipeline', action.pipeline) as PipelineEditState

    case ActionTypes.ResetState:
      return state
        .set('error', undefined)
        .set('isReady', false)
        .set('pipeline', new BrokeragePipeline()) as PipelineEditState

    case ActionTypes.SavePipelineFailure:
      return state
        .set('error', action.error)
        .set('isSaving', false) as PipelineEditState

    case ActionTypes.SavePipelineRequest:
      return state.set('isSaving', true) as PipelineEditState

    case ActionTypes.SavePipelineSuccess:
      return state
        .set('error', undefined)
        .set('isSaving', false) as PipelineEditState

    case ActionTypes.SetBuyers:
      return state.setIn(
        ['pipeline', 'buyers'],
        action.buyers,
      ) as PipelineEditState

    case ActionTypes.SetBuyersLender:
      return state.setIn(
        ['pipeline', 'buyerLender'],
        action.buyersLender,
      ) as PipelineEditState

    case ActionTypes.SetBuyersTitleCompany:
      return state.setIn(
        ['pipeline', 'buyersTitleCompany'],
        action.buyersTitleCompany,
      ) as PipelineEditState

    case ActionTypes.SetClosingDate:
      return state.setIn(
        ['pipeline', 'closingDate'],
        action.closingDate,
      ) as PipelineEditState

    case ActionTypes.SetCriticalDate: {
      const index = state.pipeline.criticalDates.findIndex(
        (cd) => cd !== undefined && cd.id === action.criticalDate.id,
      )
      if (index >= 0) {
        return state.setIn(
          ['pipeline', 'criticalDates', index],
          action.criticalDate,
        ) as PipelineEditState
      } else {
        return state.setIn(
          ['pipeline', 'criticalDates'],
          state.pipeline.criticalDates.push(action.criticalDate),
        ) as PipelineEditState
      }
    }

    case ActionTypes.SetCriticalDates:
      const criticalDates = action.criticalDates.toJS() as Assignment[]
      const criticalDatesIdd = List(
        criticalDates.map((cd, index) => {
          return Assignment.fromJS({
            ...cd,
            id: `criticalDate-${Date.now()}-${index}`,
          })
        }),
      )

      return state.setIn(
        ['pipeline', 'criticalDates'],
        List(criticalDatesIdd),
      ) as PipelineEditState

    case ActionTypes.SetCriticalDateComplete: {
      const index = state.pipeline.criticalDates.findIndex(
        (cd) => cd !== undefined && cd.id === action.criticalDateId,
      )
      if (index >= 0) {
        return state.setIn(
          ['pipeline', 'criticalDates', index, 'isComplete'],
          action.isComplete,
        ) as PipelineEditState
      } else {
        return state
      }
    }

    case ActionTypes.SetDueDiligenceExpirationDate:
      return state.setIn(
        ['pipeline', 'dueDiligenceExpirationDate'],
        action.dueDiligenceExpirationDate,
      ) as PipelineEditState

    case ActionTypes.SetEarnestMoney:
      return state.setIn(
        ['pipeline', 'earnestMoney'],
        action.earnestMoney,
      ) as PipelineEditState

    case ActionTypes.SetEarnestMoneyDepositDate:
      return state.setIn(
        ['pipeline', 'earnestMoneyDepositDate'],
        action.earnestMoneyDepositDate,
      ) as PipelineEditState

    case ActionTypes.SetEarnestMoneyLocation:
      return state.setIn(
        ['pipeline', 'earnestMoneyLocation'],
        action.earnestMoneyLocation,
      ) as PipelineEditState

    case ActionTypes.SetEarnestMoneyWithdrawlDate:
      return state.setIn(
        ['pipeline', 'earnestMoneyWithdrawlDate'],
        action.earnestMoneyWithdrawlDate,
      ) as PipelineEditState

    case ActionTypes.SetEffectiveDate:
      return state.setIn(
        ['pipeline', 'effectiveDateOfOffer'],
        action.effectiveDate,
      ) as PipelineEditState
    
    case ActionTypes.SetNotes:
      return state.setIn(
        ['pipeline', 'notes'],
        action.notes,
    ) as PipelineEditState

    case ActionTypes.SetIs1031Exchange:
      return state.setIn(
        ['pipeline', 'is1031Exchange'],
        action.is1031Exchange,
      ) as PipelineEditState

    case ActionTypes.SetLeasePriceTerm: {
      const newLeasePriceBreakdown =
        action.index !== undefined &&
        state.pipeline.leasePriceBreakdown.has(action.index)
          ? state.pipeline.leasePriceBreakdown.set(action.index, action.term)
          : state.pipeline.leasePriceBreakdown.push(action.term)

      return state.setIn(
        ['pipeline', 'leasePriceBreakdown'],
        newLeasePriceBreakdown,
      ) as PipelineEditState
    }

    case ActionTypes.SetListingAgents:
      return state.setIn(
        ['pipeline', 'listingAgents'],
        action.listingAgents,
      ) as PipelineEditState

    case ActionTypes.SetListingFee:
      return state.setIn(
        ['pipeline', 'listingFee'],
        action.listingFee,
      ) as PipelineEditState

    case ActionTypes.SetName:
      return state.setIn(['pipeline', 'name'], action.name) as PipelineEditState

    case ActionTypes.SetNoListingAgent:
      return state.setIn(
        ['pipeline', 'noListingAgent'],
        action.noListingAgent,
      ) as PipelineEditState

    case ActionTypes.SetNoSellingAgent:
      return state.setIn(
        ['pipeline', 'noSellingAgent'],
        action.noSellingAgent,
      ) as PipelineEditState

    case ActionTypes.SetSalesPrice:
      return state.setIn(
        ['pipeline', 'salesPrice'],
        action.salesPrice,
      ) as PipelineEditState

    case ActionTypes.SetSellers:
      return state.setIn(
        ['pipeline', 'sellers'],
        action.sellers,
      ) as PipelineEditState

    case ActionTypes.SetSellersMortgage:
      return state.setIn(
        ['pipeline', 'sellerMortgage'],
        action.sellersMortgage,
      ) as PipelineEditState

    case ActionTypes.SetSellersTitleCompany:
      return state.setIn(
        ['pipeline', 'sellersTitleCompany'],
        action.sellersTitleCompany,
      ) as PipelineEditState

    case ActionTypes.SetSellingAgents:
      return state.setIn(
        ['pipeline', 'sellingAgents'],
        action.sellingAgents,
      ) as PipelineEditState

    case ActionTypes.SetSellingFee:
      return state.setIn(
        ['pipeline', 'sellingFee'],
        action.sellingFee,
      ) as PipelineEditState

    case ActionTypes.SetStatus:
      return state.setIn(
        ['pipeline', 'status'],
        action.status,
      ) as PipelineEditState

    case ActionTypes.ToggleShowCriticalDateModal:
      return state.set(
        'showCriticalDateModal',
        action.show,
      ) as PipelineEditState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* beginAddCriticalDateSaga() {
  try {
    const pipeline: BrokeragePipeline = yield select(selectPipeline())

    const newAssignment = new Assignment({
      id: `criticalDate-${Date.now()}`,
      properties: List<AssociatedProperty>([pipeline.associatedProperty]),
      source: new AssignmentSource({
        name: pipeline.name,
        sourceId: pipeline.id,
        type: AssignmentSourceType.BrokeragePipeline,
      }),
    })

    yield put(assignmentFormActions.initializeAssignment(newAssignment))
    yield put(toggleShowCriticalDateModal(true))
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* beginEditCriticalDateSaga(action: IBeginEditCriticalDateAction) {
  try {
    yield put(assignmentFormActions.initializeAssignment(action.criticalDate))
    yield put(toggleShowCriticalDateModal(true))
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* cancelEditCriticalDateSaga() {
  try {
    yield put(toggleShowCriticalDateModal(false))
    yield put(assignmentFormActions.resetStore())
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* endEditCriticalDateSaga() {
  try {
    let criticalDate: Assignment = yield select(selectAssignment())

    criticalDate = criticalDate.set(
      'markup',
      convertToMarkup(criticalDate.markupState.getCurrentContent()),
    ) as Assignment

    yield put(setCriticalDate(criticalDate))
    yield put(toggleShowCriticalDateModal(false))
    yield put(assignmentFormActions.resetStore())
  } catch (err) {
    yield put(errorToast('Oops!', err.message))
  }
}

function* checkPipelineExistsSaga(action: ICheckPipelineExistsAction) {
  try {
    const result = yield makeAuthenticatedRequest(
      `api/BrokeragePipelines/Listing/${action.listingId}`,
    )
    yield put(checkPipelineExistsSuccess(result.count > 0 ? true : false))
  } catch (err) {
    yield put(checkPipelineExistsFailure(err))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadAddPipelineSaga(action: ILoadAddPipelineAction) {
  try {
    yield put(loadPipelineRequest())

    const criticalDates: List<IBrokeragePipelineCriticalDates> = yield select(
      selectCriticalDateOptions(),
    )

    const criticalDateOptions: List<IBrokeragePipelineCriticalDates> = criticalDates.toJS()

    const result = action.quickAdd
      ? {
          success: true,
          entity: new BrokeragePipeline({
            quickAdd: true,
            criticalDates: criticalDateOptions.find(
              (x) => x?.type === ListingType.Sale,
            )?.criticalDates,
          }).toJS(),
        }
      : yield makeAuthenticatedRequest(
          `/api/BrokeragePipelines/${action.listingId}/New`,
        )

    if (result.success === true) {
      if (result.entity && Array.isArray(result.entity.criticalDates)) {
        result.entity.criticalDates = result.entity.criticalDates.map(
          (cd: any, i: number) => {
            cd.id = `criticalDate-${Date.now()}-${i}`
            return cd
          },
        )
      }

      yield put(loadPipelineSuccess(BrokeragePipeline.fromJS(result.entity)))
    } else {
      yield put(loadPipelineFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadPipelineFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* loadEditPipelineSaga(action: ILoadEditPipelineAction) {
  try {
    yield put(loadPipelineRequest())

    const result = yield makeAuthenticatedRequest(
      `/api/BrokeragePipelines/${action.pipelineId}`,
    )

    if (result.success === true) {
      yield put(loadPipelineSuccess(BrokeragePipeline.fromJS(result.entity)))
    } else {
      yield put(loadPipelineFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadPipelineFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* savePipelineSaga(action: ISavePipelineAction) {
  try {
    yield put(savePipelineRequest())

    const pipeline = action.pipeline.toJS()

    const reqOpts: IRequestOptions = {
      body: JSON.stringify(pipeline),
      method: pipeline.id ? RequestMethod.PUT : RequestMethod.POST,
    }

    const reqUrl = pipeline.id
      ? `/api/BrokeragePipelines/${pipeline.id}`
      : '/api/BrokeragePipelines'

    const result = yield makeAuthenticatedRequest(reqUrl, reqOpts)

    if (result.success === true) {
      yield put(savePipelineSuccess())
      yield put(push(`/brokerage/pipeline/${pipeline.id || result.entity.id}`))
      yield put(successToast('Success!', `${pipeline.name} saved`))
    } else {
      yield put(savePipelineFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(savePipelineFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchBeginAddCriticalDate() {
  yield takeLatest(ActionTypes.BeginAddCriticalDate, beginAddCriticalDateSaga)
}

function* watchBeginEditCriticalDate() {
  yield takeLatest(ActionTypes.BeginEditCriticalDate, beginEditCriticalDateSaga)
}

function* watchCancelEditCriticalDate() {
  yield takeLatest(
    ActionTypes.CancelEditCriticalDate,
    cancelEditCriticalDateSaga,
  )
}

function* watchEndEditCriticalDate() {
  yield takeLatest(ActionTypes.EndEditCriticalDate, endEditCriticalDateSaga)
}

function* watchLoadAddPipeline() {
  yield takeLatest(ActionTypes.LoadAddPipeline, loadAddPipelineSaga)
}

function* watchCheckPipelineExists() {
  yield takeLatest(ActionTypes.CheckPipelineExists, checkPipelineExistsSaga)
}

function* watchLoadEditPipeline() {
  yield takeLatest(ActionTypes.LoadEditPipeline, loadEditPipelineSaga)
}

function* watchSavePipeline() {
  yield takeLatest(ActionTypes.SavePipeline, savePipelineSaga)
}

export function* pipelineEditRootSaga() {
  yield fork(watchBeginAddCriticalDate)
  yield fork(watchBeginEditCriticalDate)
  yield fork(watchCancelEditCriticalDate)
  yield fork(watchEndEditCriticalDate)
  yield fork(watchLoadAddPipeline)
  yield fork(watchLoadEditPipeline)
  yield fork(watchSavePipeline)
  yield fork(watchCheckPipelineExists)
}

// -------------------------
// SELECTORS
// -------------------------
const selectConfigurationState = () => (state: ApplicationState) =>
  state.global.configuration

export const selectCriticalDateOptions = () =>
  createSelector(
    selectConfigurationState(),
    (config) => config.brokeragePipelineCriticalDates,
  )

const selectModuleState = () => (state: ApplicationState) =>
  state.brokeragePipelineEdit

export const selectEditCriticalDateIndex = () =>
  createSelector(selectModuleState(), (mState) => mState.editCriticalDateIndex)

export const selectError = () =>
  createSelector(selectModuleState(), (mState) => mState.error)

export const selectIsLoading = () =>
  createSelector(selectModuleState(), (mState) => mState.isLoading)

export const selectCheckPipelineExists = () =>
  createSelector(selectModuleState(), (mState) => mState.hasExistingPipeline)

export const selectIsReady = () =>
  createSelector(selectModuleState(), (mState) => mState.isReady)

export const selectIsSaving = () =>
  createSelector(selectModuleState(), (mState) => mState.isSaving)

export const selectPipeline = () =>
  createSelector(selectModuleState(), (mState) => mState.pipeline)

export const selectShowCriticalDateModal = () =>
  createSelector(selectModuleState(), (mState) => mState.showCriticalDateModal)

export const selectListingType = () =>
  createSelector(
    (state: ApplicationState) => state.listingEdit,
    (listingEdit) => listingEdit.listing.type,
  )
